import { Component, ViewChild } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import {
  AbstractBlModal,
  ModalSize,
  ModalReason,
  NumberPlateViewComponent,
  BlReferenceGridComponent,
  BlDialogService,
  BlLoadingService,
  ButtonCellRendererComponent,
  ButtonCellEditorComponent,
  BlFileData,
  BlModalService,
  ModalButtonType,
  TooltipCellComponent,
} from '@blcloud/bl-ng-ui-component';
import {
  DateTimeUtils, DateTimeOutput, StringUtils,
  DateTimePadding, DateSeparator, DateEra
} from '@blcloud/bl-common';
import {
  map as _map,
  isNil as _isNil,
  forEach as _forEach,
  cloneDeep as _cloneDeep,
  isEmpty as _isEmpty,
  includes as _includes,
  some as _some,
  filter as _filter,
} from 'lodash';
import { GridOptions } from 'ag-grid';
import { IProfileEntryCustomerVehicleLinkOptDtl, IFileManageInfo } from '@blcloud/bl-datamodel';
import { IDispData, CellRendererToolTipParams } from './profile-matching-modal.interface';
import { ProfileMatchingModalService } from './profile-matching-modal.service';
import {
  FileService,
} from '@blcloud/bl-ng-share-module';
import { ImageModalComponent } from '../image-modal/image-modal.component';
import { CustomerSelectGuideComponent } from '../customer-select-guide/customer-select-guide.component';
import { ProfileDataSelectModalComponent } from '../profile-data-select-modal/profile-data-select-modal.component';
import { MemoTargetDiv } from '@blcloud/bl-datamodel/enum/bizcmn/memo-target-div';
import { MemoImportantDiv } from '@blcloud/bl-datamodel/enum/bizcmn/memo-important-div';
import { ProfileEntryLinkOptVcleStatusDiv } from '@blcloud/bl-datamodel/enum/profile/profile-entry-link-opt-vcle-status-div';
import { SexDivArray } from '@blcloud/bl-datamodel/enum/common/sex-div';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';

/**
 * プロフィール登録 顧客・車両情報の紐付けコンポーネント
 */
@Component({
  templateUrl: './profile-matching-modal.component.html',
  styleUrls: ['./profile-matching-modal.component.scss'],
  providers: [ProfileMatchingModalService],
})
export class ProfileMatchingModalComponent extends AbstractBlModal {

  /** 参照用グリッド */
  @ViewChild('refGrid') refGrid: BlReferenceGridComponent;

  /** モーダルウィンドウボタンタイプ */
  readonly modalButtonType = ModalButtonType;

  /** データ */
  data: IDispData;

  /** グリッドオプション */
  _profileGridOptions: GridOptions;

  /** グリッドオプション */
  _gridOptions: GridOptions;

  /** 仮登録情報のグリッド列の設定 */
  profileColumnDefs = [
    {
      headerName: 'お客様名',
      field: 'customerName',
      minWidth: 150,
    },
    {
      headerName: 'お客様名カナ',
      field: 'customerKanaName',
      minWidth: 200,
    },
    {
      headerName: '電話番号',
      field: 'contactTelInformation',
      minWidth: 120,
    },
    {
      headerName: 'メールアドレス',
      field: 'contactEmailInformation',
      minWidth: 300,
    },
    {
      headerName: '郵便番号',
      field: 'postalCode',
      minWidth: 90,
    },
    {
      headerName: '住所',
      field: 'address',
      minWidth: 300,
    },
    {
      headerName: '性別',
      field: 'dispSexDiv',
      minWidth: 80,
      cellStyle: { 'text-align': 'center', 'padding': '9px 4px', 'justify-content': 'center' },
    },
    {
      headerName: '生年月日',
      field: 'birthDay',
      minWidth: 120,
    },
    {
      headerName: 'ナンバープレート',
      field: 'dispNumberPlate',
      minWidth: 150,
      cellRendererFramework: NumberPlateViewComponent,
    },
    {
      headerName: 'メーカー／車種',
      field: 'makerModel',
      minWidth: 200,
    },
    {
      headerName: '車検満期日',
      field: 'vcleInspDueDate',
      minWidth: 120,
    },
    {
      headerName: '車検証画像',
      field: 'image',
      minWidth: 150,
      cellStyle: { 'padding': '1px' },
      cellRendererFramework: ButtonCellRendererComponent,
      cellEditorFramework: ButtonCellEditorComponent,
    },
    {
      headerName: 'DM送信',
      field: 'dmSendDivEnumName',
      minWidth: 90,
    },
    {
      headerName: '申し送り事項',
      field: 'messageNotices',
      minWidth: 300,
      cellRendererFramework: TooltipCellComponent,
    },
  ];

  /** 顧客・車両情報の候補のグリッド列の設定 */
  ColumnDefs = [
    {
      headerName: 'お客様名',
      field: 'customerName',
      minWidth: 150,
    },
    {
      headerName: 'お客様名カナ',
      field: 'customerKanaName',
      minWidth: 200,
    },
    {
      headerName: '電話番号',
      field: 'dispTel',
      minWidth: 120,
      cellRendererFramework: TooltipCellComponent,
    },
    {
      headerName: 'メールアドレス',
      field: 'dispEmail',
      minWidth: 300,
      cellRendererFramework: TooltipCellComponent,
    },
    {
      headerName: '郵便番号',
      field: 'postalCode',
      minWidth: 90,
    },
    {
      headerName: '住所',
      field: 'address',
      minWidth: 300,
    },
    {
      headerName: '性別',
      field: 'dispSexDiv',
      minWidth: 80,
      cellStyle: { 'text-align': 'center', 'padding': '9px 4px', 'justify-content': 'center' },
    },
    {
      headerName: '生年月日',
      field: 'birthDay',
      minWidth: 120,
    },
    {
      headerName: 'ナンバープレート',
      field: 'dispNumberPlate',
      minWidth: 150,
      cellRendererFramework: NumberPlateViewComponent,
      cellClass: (params) => {
        const checkDiv = params['data'].profileEntryCustomerVehicleLinkOptDtl.profileEntryLinkOptVcleStatusDiv;
        if (checkDiv === ProfileEntryLinkOptVcleStatusDiv.CustomerOnly) {
          return 'no-vehicle';
        }
        return '';
      },
    },
    {
      headerName: 'メーカー／車種',
      field: 'makerModel',
      minWidth: 200,
      cellClass: (params) => {
        const checkDiv = params['data'].profileEntryCustomerVehicleLinkOptDtl.profileEntryLinkOptVcleStatusDiv;
        if (checkDiv === ProfileEntryLinkOptVcleStatusDiv.CustomerOnly) {
          return 'no-vehicle';
        }
        return '';
      },
    },
    {
      headerName: '車検満期日',
      field: 'vcleInspDueDate',
      minWidth: 120,
      cellClass: (params) => {
        const checkDiv = params['data'].profileEntryCustomerVehicleLinkOptDtl.profileEntryLinkOptVcleStatusDiv;
        if (checkDiv === ProfileEntryLinkOptVcleStatusDiv.CustomerOnly) {
          return 'no-vehicle';
        }
        return '';
      },
    },
    {
      headerName: '車検証画像',
      field: 'image',
      minWidth: 150,
      cellStyle: { 'padding': '1px' },
      cellRendererFramework: ButtonCellRendererComponent,
      cellEditorFramework: ButtonCellEditorComponent,
      cellClass: (params) => {
        const checkDiv = params['data'].profileEntryCustomerVehicleLinkOptDtl.profileEntryLinkOptVcleStatusDiv;
        if (checkDiv === ProfileEntryLinkOptVcleStatusDiv.CustomerOnly) {
          return 'no-vehicle';
        }
        return '';
      },
    },
    {
      headerName: '備考',
      field: 'etc',
      minWidth: 400,
      cellClass: (params) => {
        const checkDiv = params['data'].profileEntryCustomerVehicleLinkOptDtl.profileEntryLinkOptVcleStatusDiv;
        if (checkDiv === ProfileEntryLinkOptVcleStatusDiv.CustomerOnly) {
          return 'no-vehicle';
        }
        return '';
      },
    },
    {
      headerName: '顧客コード',
      field: 'customerCode',
      hide: true  // 非表示
    },
  ];

  /** グリッドページあたりの件数 */
  _gridSizePatterns: number[] = [];

  /** 詳細グリッドヘッダの高さ */
  _gridHeaderHeight = 37;

  /** 詳細グリッドの高さ */
  _gridHeight = '100%';

  /** 詳細グリッド行の高さ */
  _gridRowHeight = 35;

  /** 検索結果件数 */
  _totalCount = 0;

  /** 連絡先 */
  _tel: string;

  /** メールアドレス */
  _email: string;

  /** メモ */
  _memo: string;

  /** ボタン実行フラグ */
  _anyButtonValue = false;

  /** 選択データ */
  selectedData = null;

  /** 紐付け候補MAXサイズ */
  private readonly LIST_MAX_SIZE = 100;

  /**
   * コンストラクタ
   */
  constructor(
    private service: ProfileMatchingModalService,
    private dialogService: BlDialogService,
    private loadingService: BlLoadingService,
    private fileService: FileService,
    private modalService: BlModalService,
  ) {
    super();
    this.initGrid();
  }

  // --------------------------------------------------
  // AbstractModal Override

  /**
   * @override
   */
  defaultOption() {
    return {
      size: ModalSize.Full,
    };
  }

  /**
   * @override
   */
  getResults(): any {
    return {};
  }

  /**
   * 表示後初期化処理.
   */
  onShown() {
    this.showLoading();
    this.afterShow();
  }

  /**
   * @override
   */
  afterShow() {
    const profileEntry = this.data.profileEntry;
    const profileEntryVehicle = this.data.profileEntryVehicle;
    const profileRowData = {
      // お客様名
      customerName: StringUtils.concat(profileEntry.customerFamilyName, '　', profileEntry.customerGivenName),
      // フリガナ
      customerKanaName: StringUtils.concat(profileEntry.customerFamilyNameKana, '　', profileEntry.customerGivenNameKana),
      // 連絡先
      contactTelInformation: !_isNil(profileEntry.contactInfoTel) ? profileEntry.contactInfoTel.contactInformation : null,
      // メールアドレス
      contactEmailInformation: !_isNil(profileEntry.contactInfoEmail) ? profileEntry.contactInfoEmail.contactInformation : null,
      // 郵便番号
      postalCode: profileEntry.postalCode,
      // 住所
      address: StringUtils.concat(
        profileEntry.customerAddressStateCity ? profileEntry.customerAddressStateCity : '',
        profileEntry.customerAddressStreet ? profileEntry.customerAddressStreet : '',
        ' ',
        profileEntry.customerAddressBuildingName ? profileEntry.customerAddressBuildingName : ''
      ),
      // 性別
      dispSexDiv: SexDivArray.filter(r => {
        return r.value === profileEntry.sexDiv;
      })[0].text,
      // 生年月日
      birthDay: !_isNil(profileEntry.birthDay) ?
        DateTimeUtils.format(
          profileEntry.birthDay,
          DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP
        ) : '',
      // ナンバープレート(車両登録番号)
      dispNumberPlate: this.createItemLabelNumPlate(
        profileEntryVehicle.vcleInspRegistOfficeName,
        profileEntryVehicle.numPlateClassNum,
        profileEntryVehicle.numPlateKana,
        profileEntryVehicle.numPlateSeriesSpecifNum,
        null,
      ),
      // メーカー／車種
      makerModel: StringUtils.concatWith(
        '／',
        profileEntryVehicle.blVehicleMakerFullWFixNm,
        profileEntryVehicle.vehicleModelFixFullWName
      ),
      // 車検満期日
      vcleInspDueDate: !_isNil(profileEntryVehicle.vcleInspDueDate) ?
        DateTimeUtils.format(
          profileEntryVehicle.vcleInspDueDate,
          DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP
        ) : '',
      // DM送信
      dmSendDivEnumName: profileEntry.dmSendDivEnumName,
      image: {
        caption: '確認',
        classes: 'btn btn-default',
        onClick: this.onClickImageOpen.bind(this, [profileEntryVehicle.vcleInspCertifImageFileMgt]),
        // 画像の有無で活性を制御する
        disabled: _isNil(profileEntryVehicle.vcleInspCertifImageFileMgt) ? true : false,
      },
      // 申し送り事項
      messageNotices: {
        value: profileEntry.messageNotices,
        tooltip: profileEntry.messageNotices,
        placement: 'bottom',
        tooltipClass: 'profile-matching-tooltip-inner',
      },
    };
    this._tel = !_isNil(profileEntry.contactInfoTel) ? profileEntry.contactInfoTel.contactInformation : null;
    this._email = !_isNil(profileEntry.contactInfoEmail) ? profileEntry.contactInfoEmail.contactInformation : null;
    this._memo = !_isEmpty(profileEntry.messageNotices) ? profileEntry.messageNotices : null;
    this._profileGridOptions.api.setRowData([profileRowData]);
    // 紐付候補リスト設定
    this.setRowData(this.data.profileEntryCustomerVehicleLinkOptDtlList);

    this.hideLoading();
  }

  /**
   * 紐付ける顧客・車両情報の候補の行データを設定
   * @param profileEntryCustomerVehicleLinkOptDtlList 紐付候補リスト
   * @param size 検索数
   */
  private setRowData(profileEntryCustomerVehicleLinkOptDtlList: IProfileEntryCustomerVehicleLinkOptDtl[]): void {

    // 紐付候補リストから表示用の行データを作成
    let rowData = [];
    // プロフィール登録来店車両情報一致データが存在する場合はそれだけ表示
    if (_some(profileEntryCustomerVehicleLinkOptDtlList, data => data.profileEntryVisitVehicleInfoMatchFlag === true)) {
      rowData = this.createRowData(
        _filter(profileEntryCustomerVehicleLinkOptDtlList, data => data.profileEntryVisitVehicleInfoMatchFlag === true)
      );
    } else {
      rowData = this.createRowData(profileEntryCustomerVehicleLinkOptDtlList);
    }
    // 行データを設定
    this._gridOptions.api.setRowData(rowData);
    // 検索結果件数を設定
    this._totalCount = rowData.length;
    // 顧客の重複を除いた件数をカウントする
    const customerList = [];
    _forEach(rowData, data => {
      const checkCustomer = _some(customerList, customerCode => customerCode === data.customerCode);
      if (!checkCustomer && !_isNil(data.customerCode)) {
        customerList.push(data.customerCode);
      }
    });

    if (customerList.length >= this.LIST_MAX_SIZE) {
      this.dialogService.info(
        '候補の顧客が100件を超えた為、100件のみ表示しています。'
        + '<br>※該当する顧客が表示されていない場合は、'
        + '<br>　一度「新規登録」を行った後に顧客車両検索にて再度ご確認下さい。'
      ).subscribe(
        modalInfo => {
          modalInfo.hide();
          this.loadingService.hide();
        }
      );
    }

    // リサイズ処理
    this.resizeGrid();
  }

  /**
   * 紐付候補リストから表示用の行データを作成
   * @param profileEntryCustomerVehicleLinkOptDtlList 紐付候補リスト
   */
  private createRowData(profileEntryCustomerVehicleLinkOptDtlList: IProfileEntryCustomerVehicleLinkOptDtl[]): any {
    return _map(profileEntryCustomerVehicleLinkOptDtlList, data => {
      // メモ対象区分
      data.memoTargetDiv = MemoTargetDiv.Customer;
      // メモ重要区分
      data.memoImportantDiv = MemoImportantDiv.Normal;
      // 商談メモ本文
      data.dealsMemoText = this._memo;
      // 先頭の電話番号はプロフィールと一致するもの
      const telList = _map(data.contactInfoTelList, item => item.contactInformation);
      const telListStr = StringUtils.concatWith('　', ...telList);
      let topTel = _includes(telList, this._tel) ? this._tel : '';
      // セットされていなければ1行目を入れておく
      if (topTel === '' && !_isEmpty(data.contactInfoTelList)) {
        topTel = data.contactInfoTelList[0].contactInformation;
      }

      const telParams: CellRendererToolTipParams = {
        value: topTel,
        tooltip: telListStr,
        placement: 'bottom',
      };

      const emailList = _map(data.contactInfoEmailList, item => item.contactInformation);
      const emailListStr = StringUtils.concatWith('　', ...emailList);
      let topEmail = _includes(emailList, this._email) ? this._email : '';
      // セットされていなければ1行目を入れておく
      if (topEmail === '' && !_isEmpty(data.contactInfoEmailList)) {
        topEmail = data.contactInfoEmailList[0].contactInformation;
      }
      const emailParams: CellRendererToolTipParams = {
        value: topEmail,
        tooltip: emailListStr,
        placement: 'bottom',
      };
      let etcMessage = data.logicalDeleteDiv !== LogicalDeleteDiv.Valid ? '※この顧客は削除されています' : '';
      if (data.profileEntryVehicleMgtNumberMatchFlag || data.profileEntryVisitVehicleInfoMatchFlag) {
        if (etcMessage !== '') {
          etcMessage = etcMessage + '<br>';
        }
        if (data.profileEntryVehicleMgtNumberMatchFlag) {
          etcMessage = etcMessage + '※前回選択した車両はこちらです';
        } else if (data.profileEntryVisitVehicleInfoMatchFlag) {
          etcMessage = etcMessage + '※ご来店の際に読み取ったQRはこの車両のQRです';
        }
      }
      return {
        // お客様名
        customerName: StringUtils.concat(data.customerFamilyName, '　', data.customerGivenName),
        // フリガナ
        customerKanaName: StringUtils.concat(data.customerFamilyNameKana, '　', data.customerGivenNameKana),
        // 連絡先
        dispTel: telParams,
        // メールアドレス
        dispEmail: emailParams,
        // 郵便番号
        postalCode: data.postalCode,
        // 住所
        address: StringUtils.concat(
          data.customerAddressStateCity ? data.customerAddressStateCity : '',
          data.customerAddressStreet ? data.customerAddressStreet : '',
          ' ',
          data.customerAddressBuildingName ? data.customerAddressBuildingName : ''
        ),
        // 性別
        dispSexDiv: SexDivArray.filter(r => {
          return r.value === data.sexDiv;
        })[0].text,
        // 生年月日
        birthDay: !_isNil(data.birthDay) ?
          DateTimeUtils.format(
            data.birthDay,
            DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP
          ) : '',
        // ナンバープレート(車両登録番号)
        dispNumberPlate: this.createItemLabelNumPlate(
          data.vcleInspRegistOfficeName,
          data.numPlateClassNum,
          data.numPlateKana,
          data.numPlateSeriesSpecifNum,
          data.profileEntryLinkOptVcleStatusDiv,
        ),
        // メーカー／車種
        makerModel: StringUtils.concatWith(
          '／',
          data.blVehicleMakerFullWFixNm,
          data.vehicleModelFixFullWName
        ),
        // 車検満期日
        vcleInspDueDate: !_isNil(data.vcleInspDueDate) ?
          DateTimeUtils.format(
            data.vcleInspDueDate,
            DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP
          ) : '',
        image: {
          caption: '確認',
          classes: 'btn btn-default',
          onClick: this.onClickImageOpen.bind(this, data.attachFileInfoList),
          // 画像の有無で活性を制御する
          disabled: _isEmpty(data.attachFileInfoList) ? true : false,
        },
        // 備考
        etc: etcMessage,
        // 顧客コード
        customerCode: data.customerCode,
        // 紐付け顧客情報
        profileEntryCustomerVehicleLinkOptDtl: data,
      };
    });
  }

  /**
   * ナンバープレート確認
   * プロフィール登録紐付候補車両状態区分が
   *  新規車両,　⇒「新規車両として登録」
   *  顧客のみ　⇒「顧客のみ更新」
   *  上記以外　⇒登録されてるナンバープレート（未入力があれば「仮登録車両」、全て未入力なら空白）
   * @param values
   */
  private createItemLabelNumPlate(
    vcleInspRegistOfficeName: string,
    numPlateClassNum: string,
    numPlateKana: string,
    numPlateSeriesSpecifNum: number,
    profileEntryLinkOptVcleStatusDiv: string,
  ) {
    if (profileEntryLinkOptVcleStatusDiv === ProfileEntryLinkOptVcleStatusDiv.NewVehicle) {
      return '新規車両として登録';
    } else if (profileEntryLinkOptVcleStatusDiv === ProfileEntryLinkOptVcleStatusDiv.CustomerOnly) {
      return '顧客のみ更新';
    } else {
      if (_isNil(vcleInspRegistOfficeName)
        && _isNil(numPlateClassNum)
        && _isNil(numPlateKana)
        && (_isNil(numPlateSeriesSpecifNum) || numPlateSeriesSpecifNum === 0)
      ) {
        return '';
      } else if (numPlateSeriesSpecifNum === 0) {
        return '仮登録車両';
      } else {
        const values = [
          vcleInspRegistOfficeName,
          numPlateClassNum,
          numPlateKana,
          String(numPlateSeriesSpecifNum),
        ];
        return StringUtils.concatWithSpace(...values);
      }
    }
  }

  /**
   * グリッド初期化
   */
  private initGrid(): void {
    this._profileGridOptions = <GridOptions>{};
    this._profileGridOptions.columnDefs = this.profileColumnDefs;
    this._profileGridOptions.rowData = [];
    this._gridOptions = <GridOptions>{};
    this._gridOptions.columnDefs = this.ColumnDefs;
    this._gridOptions.rowData = [];
    this._gridOptions.paginationPageSize = 20;
    this._gridSizePatterns = [20];
  }

  /**
   * グリッドの準備完了時
   */
  onReadyGrid() {
    // リサイズ処理
    this.resizeGrid();
  }

  /**
   * リサイズ処理
   */
  private resizeGrid(): void {
    // グリッド枠いっぱいに拡大
    this._profileGridOptions.api.sizeColumnsToFit();
    this._gridOptions.api.sizeColumnsToFit();
  }

  /**
   * ページング: ページサイズ変更時処理
   * @param size ページサイズ
   */
  onChangePageSize(size: number): void {
    this._gridOptions.paginationPageSize = size;
    this.refGrid.gotoFirstPage();
  }

  /**
   * 行選択時のイベント動作
   */
  onSelectedRow(e: any) {
    // 初回は自動フォーカス分なのでこちらで指定する
    if (_isNil(this.selectedData)) {
      this._gridOptions.api.forEachNode(node => {
        // １行目は選択する
        if (node.rowIndex === 0) {
          node.setSelected(true);
          this.selectedData = node.data;
        }
        // プロフィール登録来店車両情報一致フラグかプロフィール登録車両管理番号一致フラグのあるものを初期選択(複数はない想定)
        if (node.data.profileEntryCustomerVehicleLinkOptDtl.profileEntryVisitVehicleInfoMatchFlag
          || node.data.profileEntryCustomerVehicleLinkOptDtl.profileEntryVehicleMgtNumberMatchFlag) {
          let count = 0;
          for (let i = node.rowIndex; i >= 20; i -= 20) {
            count++;
          }
          this.refGrid.gotoPage(count);
          node.setSelected(true);
          this.selectedData = node.data;
        }
      });
    } else {
      this.selectedData = e.data;
    }
  }

  /**
   * モーダル終了イベント
   * @param reason　モーダル終了理由
   */
  dismiss(reason: ModalReason) {
    this.service.isAvailableProfileMainEntryRole().subscribe(
      isAvailable => {
        if (reason === ModalReason.Done) {
          if (!isAvailable) {
            this.dialogService.info('権限がないため紐付登録できません。').subscribe(
              modalInfo => {
                modalInfo.hide();
              }
            );
          } else if (this.selectedData.profileEntryCustomerVehicleLinkOptDtl.logicalDeleteDiv !== LogicalDeleteDiv.Valid) {
            // 顧客が削除されている場合はダイアログを出す
            const message = '選択した顧客情報は削除されています。<br>顧客車両検索画面で顧客を復活させてから再度紐付処理を行ってください。'
              + '<br>　顧客コード：' + this.selectedData.customerCode
              + '<br>　顧客名称　：' + this.selectedData.customerName
              + '(' + this.selectedData.customerKanaName + '）';
            this.dialogService.info(message).subscribe(
              modalInfo => {
                modalInfo.hide();
                this.emitDismissEvent(ModalReason.Cancel);
              }
            );
          } else {
            // プロフィール登録来店車両情報一致 かつ リストに顧客コード0以外が含まれている場合はモーダルで選ばせる
            if (
              this.selectedData.profileEntryCustomerVehicleLinkOptDtl.profileEntryVisitVehicleInfoMatchFlag
              && _some(this.data.profileEntryCustomerVehicleLinkOptDtlList, customer => customer.customerCode !== 0)
            ) {
              this.modalService.show(CustomerSelectGuideComponent, {
                data: {
                  profileEntryCustomerVehicleLinkOptDtlList: this.data.profileEntryCustomerVehicleLinkOptDtlList,
                }
              }).subscribe(
                modalRef => {
                  modalRef.hide();
                  if (modalRef.reason === ModalReason.Done) {
                    const setData = _cloneDeep(this.selectedData.profileEntryCustomerVehicleLinkOptDtl);
                    setData.customerCode = modalRef.getResults().customerCode;
                    this.openProfileDataSelectModal(reason, setData);
                  }
                }
              );
            } else {
              this.openProfileDataSelectModal(reason, this.selectedData.profileEntryCustomerVehicleLinkOptDtl);
            }
          }
        } else if (reason === ModalReason.Any) {
          if (this._anyButtonValue) {
            if (!isAvailable) {
              this.dialogService.info('権限がないため破棄できません。').subscribe(
                modalInfo => {
                  modalInfo.hide();
                }
              );
            } else {
              this.dialogService.confirm('お客様情報を破棄します。<br>よろしいですか？').subscribe(
                modalRef => {
                  modalRef.hide();
                  if (modalRef.reason === ModalReason.Done) {
                    this.loadingService.show();
                    this.service.startProfileEntryDiscard(this.data.profileEntry.holdProfileEntry).subscribe(() => {
                      this.dialogService.info('破棄処理が完了しました。').subscribe(
                        modalInfo => {
                          modalInfo.hide();
                          this.loadingService.hide();
                          this.emitDismissEvent(reason);
                        }
                      );
                    });
                  }
                }
              );
            }
          } else {
            if (!isAvailable) {
              this.dialogService.info('権限がないため新規登録できません。').subscribe(
                modalInfo => {
                  modalInfo.hide();
                }
              );
            } else {
              this.dialogService.confirm('新規で登録します。<br>よろしいですか？').subscribe(
                modalRef => {
                  modalRef.hide();
                  if (modalRef.reason === ModalReason.Done) {
                    this.startProfileMatching(reason, {});
                  }
                }
              );
            }
          }
        } else {
          this.emitDismissEvent(reason);
        }
      }
    );
  }

  /**
   * 採用データ選択モーダルイベント
   * @param reason　モーダル終了理由
   * @param setData　プロフィール登録情報顧客車両紐付候補(詳細)
   */
  openProfileDataSelectModal(reason: ModalReason, setData: IProfileEntryCustomerVehicleLinkOptDtl) {
    this.modalService.show(ProfileDataSelectModalComponent, {
      data: {
        profileEntry: this.data.profileEntry,
        profileEntryVehicle: this.data.profileEntryVehicle,
        profileEntryCustomerVehicleLinkOptDtl: setData,
      }
    }).subscribe(
      modalRef => {
        modalRef.hide();
        if (modalRef.reason === ModalReason.Done) {
          this.startProfileMatching(reason, modalRef.getResults());
        }
      }
    );
  }

  /**
   * 顧客紐付け実行イベント
   * @param reason　モーダル終了理由
   * @param setData　プロフィール登録情報顧客車両紐付候補(詳細)
   */
  startProfileMatching(reason: ModalReason, setData: IProfileEntryCustomerVehicleLinkOptDtl) {
    this.loadingService.show();
    this.service.startProfileMatching(
      this.data.profileEntry,
      setData,
    ).subscribe(
      () => {
        let message = '';
        if (_isEmpty(setData)) {
          message = '新規登録が完了しました。';
        } else {
          message = '顧客車両紐付けが完了しました。';
        }
        this.dialogService.info(message).subscribe(
          modalInfo => {
            modalInfo.hide();
            this.loadingService.hide();
            this.emitDismissEvent(reason);
          }
        );
      },
      (error) => {
        if (error.status === 400) {
          this.dialogService.info(error.messages[0]).subscribe(
            modalInfo => {
              modalInfo.hide();
              this.loadingService.hide();
            }
          );
        } else {
          throw error;
        }
      }
    );
  }

  /**
   * 車検証画像確認ボタン押下処理
   * @param fileManageInfos 画像データ
   */
  private onClickImageOpen(fileManageInfos: IFileManageInfo[]) {
    if (_isNil(fileManageInfos)) {
      return;
    }
    // 二重起動防止用loading
    this.loadingService.show();
    this.fileService.downloadList(fileManageInfos).subscribe(files => {
      this.readfiles(files).subscribe(result => {
        this.loadingService.hide();
        const srcList = [];
        _forEach(
          result, file => {
            srcList.push(file.data);
          }
        );
        this.modalService.show(ImageModalComponent, { data: { src: srcList } }).subscribe(
          modalRef => {
            modalRef.hide();
          }
        );
      });
    });
  }

  /**
   * ファイル読み込み処理
   * @param file ファイル
   * @returns ファイルデータ
   */
  private readfiles(files: File[]): Observable<BlFileData[]> {

    return Observable.create(observer => {

      const fileDatas: BlFileData[] = [];

      for (let i = 0; i < files.length; i++) {
        const fileData = new BlFileData(files[i]);
        const reader = new FileReader();
        reader.onload = () => {
          fileData.data = reader.result;
          fileDatas.push(fileData);
          if (fileDatas.length === files.length) {
            observer.next(fileDatas);
            observer.complete();
          }
        };
        reader.readAsDataURL(files[i]);
      }
    });
  }

  /**
   * 破棄ボタン押下
   */
  onDiscard() {
    this._anyButtonValue = true;
    this.dismiss(ModalReason.Any);
  }

  /**
   * 新規登録ボタン押下
   */
  onCreate() {
    this._anyButtonValue = false;
    this.dismiss(ModalReason.Any);
  }
}



// WEBPACK FOOTER //
// ./src/app/page/profile-entries-page/profile-matching-modal/profile-matching-modal.component.ts